import type { ComponentType } from 'react';
import type { CollectionKey, SectionKey } from '../../lib/contentConfig';
import { BaseModuleForm, CategoryForm, ZombieForm } from './EntityForms';
import { ItemForm } from './ItemForm';
import { LootTableForm } from './LootTableForm';
import { RecipeForm } from './RecipeForm';
import { BossForm } from './BossForm';
import { CosmeticForm, MissionForm, TriggerForm } from './TriggerForms';
import {
  BasesForm,
  NoiseRadiiForm,
  RaidRulesForm,
  SquadRulesForm,
  SurvivalForm,
} from './SectionForms';
import { ConditionsForm } from './ConditionsForm';
import { BeatChainForm } from '../story/BeatChainForm';
import { AllyForm, NpcForm, PanelRulesForm, PronounSetForm, StoryRulesForm } from './StoryForms';
import { ScenarioForm, SetPieceForm, WorldFlagForm } from './ScenarioForms';
import { CrewForm, HumanForm, OccupiedTellForm } from './s2/OccupiedForms';
import { KeyPartForm, KeyPartRulesForm, SiteKindForm } from './s2/KeyPartForms';
import { CraftingForm, FieldKitForm, TechTierForm } from './s2/TechForms';
import { HoldoutRulesForm } from './s2/HoldoutRulesForm';
import { ChanceEventsForm } from './s2/ChanceForms';
import { OccupiedRulesForm } from './s2/OccupiedRulesForm';
import { RescuesForm } from './s2/RescueForms';
import { HoldoutForm } from './s2/HoldoutForm';
import { VehicleForm } from './s3/VehicleForm';
import { ArenaTemplateForm } from './s3/ArenaTemplateForm';
import { ArenaRulesForm, LureSitesForm } from './s3/ArenaRulesForms';
import { OverwatchForm } from './s3/OverwatchForm';
import { ArcForm } from './s4/ArcForm';
import { StoryIssueRulesForm } from './s4/StoryIssueRulesForm';
import { BackgroundForm } from './hb/StatsForms';
import { CoreStatForm, PerkForm, SkillForm, SkillRulesForm } from './skills/SkillForms';
import {
  BuildCategoryForm,
  HomeRulesForm,
  InventoryRulesForm,
  LoadoutRulesForm,
  StorageGroupForm,
} from './hb/HomeForms';
import { EffectRulesForm, StatusEffectForm, ZoneForm } from './effects/EffectForms';
import { HealthRulesForm } from './health/HealthRulesForm';
import { NamesForm } from './names/NamesForm';
import { CharactersForm, ResurrectionForm, StoreForm } from './characters/CharacterForms';
import { WindTierForm } from './wind/WindForms';
import type { FormProps } from './types';

// The forms are typed against the generated schema; the editor hands them untyped draft JSON.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyForm = ComponentType<FormProps<any>>;

/*
 * JSON-editor-only (no structured form yet): room templates, and the Teams on the Map blocks added in F0:
 * survivorTraits (collection) and teamRules, discovery, container, convoy, encounters,
 * worldEvents (sections). The store has a small form for product kind and resurrection tier (ADR 0069); the rest of it is JSON. Squad rules (ADR 0027) and bases (ADR 0030) have a form. A section an old draft doesn't have yet opens with its C# defaults
 * (lib/teamsDefaults.ts), and the draft only changes on "Apply to draft".
 */

/** Structured forms per collection. Collections without one (room templates) use the JSON editor only. */
export const collectionForms: Partial<Record<CollectionKey, AnyForm>> = {
  items: ItemForm,
  lootTables: LootTableForm,
  recipes: RecipeForm,
  baseModules: BaseModuleForm,
  zombies: ZombieForm,
  winds: WindTierForm,
  categories: CategoryForm,
  bosses: BossForm,
  triggers: TriggerForm,
  missions: MissionForm,
  cosmetics: CosmeticForm,
  beatChains: BeatChainForm,
  allies: AllyForm,
  scenarios: ScenarioForm,
  setPieces: SetPieceForm,
  worldFlags: WorldFlagForm,
  npcs: NpcForm,
  pronounSets: PronounSetForm,
  crews: CrewForm,
  humans: HumanForm,
  tells: OccupiedTellForm,
  holdouts: HoldoutForm,
  techTiers: TechTierForm,
  siteKinds: SiteKindForm,
  keyParts: KeyPartForm,
  vehicles: VehicleForm,
  arenaTemplates: ArenaTemplateForm,
  arcs: ArcForm,
  coreStats: CoreStatForm,
  skills: SkillForm,
  perks: PerkForm,
  backgrounds: BackgroundForm,
  statusEffects: StatusEffectForm,
  zones: ZoneForm,
  storageGroups: StorageGroupForm,
  buildCategories: BuildCategoryForm,
};

export const sectionForms: Partial<Record<SectionKey, AnyForm>> = {
  raidRules: RaidRulesForm,
  survival: SurvivalForm,
  bases: BasesForm,
  noiseRadii: NoiseRadiiForm,
  squadRules: SquadRulesForm,
  conditions: ConditionsForm,
  story: StoryRulesForm,
  panelRules: PanelRulesForm,
  chanceEvents: ChanceEventsForm,
  occupied: OccupiedRulesForm,
  rescues: RescuesForm,
  holdoutRules: HoldoutRulesForm,
  crafting: CraftingForm,
  fieldKit: FieldKitForm,
  keyPartRules: KeyPartRulesForm,
  arenaRules: ArenaRulesForm,
  lureSites: LureSitesForm,
  overwatch: OverwatchForm,
  storyRules: StoryIssueRulesForm,
  skillRules: SkillRulesForm,
  names: NamesForm,
  store: StoreForm,
  characters: CharactersForm,
  resurrection: ResurrectionForm,
  effectRules: EffectRulesForm,
  healthRules: HealthRulesForm,
  homeRules: HomeRulesForm,
  inventoryRules: InventoryRulesForm,
  loadoutRules: LoadoutRulesForm,
};
